import { EditableText, defineBlock } from "eddev/blocks"
import { Container, Grid, Col } from "@components/layout/Grid"
import { ResponsiveImageFragment } from "../../types.graphql"
import { useMeasure } from "react-use"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Swiper, SwiperSlide } from "@components/atoms/Swiper"
import { twMerge } from "tailwind-merge"
import { useState } from "react"

export const meta: BlockMeta = {
  title: "Pillars",
  category: "common",
  icon: "ellipsis",
  keywords: ["pillars", "advocacy"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

export default defineBlock("general/card-pillars", (props) => {
  return (
    <Container className="py-6">
      <Grid mode="grid">
        <Col initial={12}>
          <style
            dangerouslySetInnerHTML={{
              __html: `
            .card-pillars-swiper {
              overflow: visible !important;
              padding-bottom: 20px;

              div[class^='swiper-button'] {
                top: auto;
                bottom: 0;
                right: 0;
                left: auto;
                width: 27px;
                height: 27px;
                color: #222222;
                box-shadow: inset 0px 0px 0px 1px #222222;
                border-radius: 6px;
                transition: all .35s cubic-bezier(.19,1,.22,1);

                &:after {
                  font-size: 16px !important;
                }

                svg {
                  width: 5px;
                  height: auto;

                  path {
                    stroke: currentColor;
                    stroke-width: 2px;
                  }
                }

                &:hover {
                  background: #5E5E5E;
                  box-shadow: inset 0px 0px 0px 1px #5E5E5E;
                  color: #FFFFFF;
                }

                &:active {
                  background: #222222;
                  box-shadow: inset 0px 0px 0px 1px #222222;
                  color: #FFFFFF;
                }

                &:disabled {
                  background: transparent;
                  box-shadow: inset 0px 0px 0px 1px #8E8E8E;
                  color: #8E8E8E;
                }
              }

              .swiper-button-prev {
                margin-right: 36px;
              }
            }

            @media only screen and (min-width: 1080px) {
              .card-pillars-swiper {
                padding-bottom: 0;

                div[class^='swiper-button'] {
                  display: none;
                }
              }
            }
          `,
            }}
          />
          <Swiper
            className="card-pillars-swiper"
            spaceBetween={24}
            autoplay={{
              delay: 5000,
            }}
            navigation
            breakpoints={{
              1080: {
                slidesPerView: props?.hero4 ? 4 : 3,
                initialSlide: 0,
                centeredSlides: false,
              },
              800: {
                slidesPerView: props?.hero4 ? 3.25 : 2.25,
                initialSlide: 0,
                centeredSlides: false,
              },
              0: {
                slidesPerView: 1.25,
                initialSlide: 1,
                centeredSlides: true,
              },
            }}
          >
            <SwiperSlide>
              <Pillar count={1} hero={props?.hero1 ?? undefined} textOverlay={props?.useTextOverlay ? true : false} />
            </SwiperSlide>
            <SwiperSlide>
              <Pillar count={2} hero={props?.hero2 ?? undefined} textOverlay={props?.useTextOverlay ? true : false} />
            </SwiperSlide>
            <SwiperSlide>
              <Pillar count={3} hero={props?.hero3 ?? undefined} textOverlay={props?.useTextOverlay ? true : false} />
            </SwiperSlide>
            {props?.hero4 && (
              <SwiperSlide>
                <Pillar count={4} hero={props.hero4 ?? undefined} textOverlay={props?.useTextOverlay ? true : false} />
              </SwiperSlide>
            )}
          </Swiper>
        </Col>
      </Grid>
    </Container>
  )
})

const Pillar = ({
  count,
  hero,
  textOverlay,
}: {
  count: number
  hero?: ResponsiveImageFragment
  textOverlay?: boolean
}) => {
  const ratio = textOverlay ? 272 / 360 : 368 / 240
  const [textOpen, setTextOpen] = useState<boolean>(false)

  return (
    <div
      className={twMerge(
        "bg-black text-white rounded-xl flex w-full relative p-0 box-border rounded-lg overflow-hidden mb-6 group",
        "[&_h5]:text-grey [&_h5]:type-label-caps [&_h5]:m-0 [&_h3]:type-title-m [&_h3]:m-0",
      )}
    >
      <div className="flex flex-col gap-2 h-full absolute z-10 p-5 top-0 left-0 w-full">
        <EditableText
          store={`subtitle_${count.toString()}}`}
          className="!type-label-caps"
          as="h5"
          defaultValue="Advocacy"
          placeholder="Advocacy"
        />
        <EditableText store={`title_${count.toString()}}`} as="h3" placeholder="Enter title..." />
        {env.admin && textOverlay && (
          <EditableText
            store={`content_${count.toString()}}`}
            as="p"
            className="leading-4"
            placeholder="Enter text..."
          />
        )}
      </div>
      <div className="relative w-full z-0 transition duration-700 ease-menu before:content-[''] before:absolute before:inset-0 before:z-[1] before:[background:linear-gradient(180deg,rgba(0,0,0,0.9)_0%,rgba(0,0,0,0)_100%)]">
        <ResponsiveImage {...hero} aspect={ratio} className="w-full" />
      </div>

      {!env.admin && textOverlay && (
        <>
          <button
            onClick={() => setTextOpen(!textOpen)}
            className="w-6 h-6 rounded-sm border border-white border-solid flex items-center justify-center absolute bottom-5 right-5 text-white hover:text-black hover:bg-yellow hover:border-yellow aaa-animate z-20"
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.45801 0C6.80319 0 7.08301 0.279822 7.08301 0.625V5.83301H12.292C12.6369 5.83318 12.9168 6.11309 12.917 6.45801C12.917 6.80308 12.637 7.08283 12.292 7.08301H7.08301V12.292C7.08283 12.637 6.80308 12.917 6.45801 12.917C6.11309 12.9168 5.83318 12.6369 5.83301 12.292V7.08301H0.625C0.279971 7.08283 -1.50835e-08 6.80308 0 6.45801C0.000175907 6.11309 0.280079 5.83318 0.625 5.83301H5.83301V0.625C5.83301 0.279931 6.11298 0.000175944 6.45801 0Z"
                fill="currentColor"
              />
            </svg>
          </button>

          <div
            className={twMerge(
              "bg-[#222222] w-full h-full absolute top-0 left-0 z-[9] p-5 aaa-animate pt-[40%]",
              textOpen ? "translate-y-0" : "translate-y-full group-hover:translate-y-[90%]",
            )}
          >
            <EditableText
              store={`content_${count.toString()}}`}
              as="p"
              className="leading-4"
              placeholder="Enter text..."
            />
          </div>
        </>
      )}
    </div>
  )
}
